import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { DeliveryNoteProofOfDeliveryService } from './delivery-note-proof-of-delivery.service';
import {
  DeliveryNoteProofOfDeliveryDownloadFile,
  DeliveryNoteProofOfDeliveryInitialState,
  DeliveryNoteProofOfDeliveryLoad,
  DeliveryNoteProofOfDeliverySave,
  DeliveryNoteProofOfDeliverySendRequest,
} from './delivery-note-proof-of-delivery.actions';
import { NotificationService } from '../../../../../common/services';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { downloadBlob } from '../../../../../common';
import { ProofOfDeliveryItem } from '../../models/delivery-note-proof-of-delivery.model';
import { HttpResponse } from '@angular/common/http';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface DeliveryNoteProofOfDeliveryStateModel extends ProofOfDeliveryItem {
  loaded: boolean;
}

const initialState: DeliveryNoteProofOfDeliveryStateModel = {
  ...new ProofOfDeliveryItem(),
  loaded: false,
};

@State<DeliveryNoteProofOfDeliveryStateModel>({
  name: 'deliveryNoteProofOfDelivery',
  defaults: initialState,
})
@Injectable()
export class DeliveryNoteProofOfDeliveryState {
  @Selector()
  static getState(state: DeliveryNoteProofOfDeliveryStateModel): DeliveryNoteProofOfDeliveryStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: DeliveryNoteProofOfDeliveryStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static isProofOfDeliveryExist(state: DeliveryNoteProofOfDeliveryStateModel): boolean {
    return Boolean(state.proofOfDeliveryView?.requestTimestamp);
  }

  constructor(
    private deliveryNoteProofOfDeliveryService: DeliveryNoteProofOfDeliveryService,
    private notificationService: NotificationService,
  ) {}

  @Action(DeliveryNoteProofOfDeliveryLoad)
  getProofOfDeliveryItem(
    ctx: StateContext<DeliveryNoteProofOfDeliveryStateModel>,
    { payload }: DeliveryNoteProofOfDeliveryLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.deliveryNoteProofOfDeliveryService.getProofOfDeliveryItem(payload).pipe(
      map((podItem: ProofOfDeliveryItem) => {
        ctx.patchState({ ...podItem, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DeliveryNoteProofOfDeliveryDownloadFile)
  downloadExportsFile(
    ctx: StateContext<DeliveryNoteProofOfDeliveryStateModel>,
    { payload }: DeliveryNoteProofOfDeliveryDownloadFile,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.deliveryNoteProofOfDeliveryService.downloadFile(payload).pipe(
      map((response: HttpResponse<Blob>) => {
        const { body } = response;
        downloadBlob(body, payload.name);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DeliveryNoteProofOfDeliverySave)
  saveProofOfDeliveryItem(
    ctx: StateContext<DeliveryNoteProofOfDeliveryStateModel>,
    { payload }: DeliveryNoteProofOfDeliverySave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.deliveryNoteProofOfDeliveryService.saveProofOfDeliveryItem(payload).pipe(
      map((podItem: ProofOfDeliveryItem) => {
        ctx.patchState({ ...podItem, loaded: true });
        this.notificationService.success('api.pod.success.create');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DeliveryNoteProofOfDeliverySendRequest)
  sendRequestProofOfDelivery(
    ctx: StateContext<DeliveryNoteProofOfDeliveryStateModel>,
    { payload }: DeliveryNoteProofOfDeliverySendRequest,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.deliveryNoteProofOfDeliveryService.sendRequestProofOfDelivery(payload).pipe(
      map(() => {
        ctx.dispatch(new DeliveryNoteProofOfDeliveryLoad(payload));
        this.notificationService.success('api.pod.success.request');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DeliveryNoteProofOfDeliveryInitialState)
  setInitialState(ctx: StateContext<DeliveryNoteProofOfDeliveryStateModel>): void {
    ctx.setState(initialState);
  }
}
